import React, { useEffect, useState } from "react";
import { useStores } from "../../../use-stores";
import { observer } from "mobx-react";
import { Box, Button, Grid2, useTheme } from "@mui/material";
import CustomTypography from "../../components/common/form/CustomTypography";
import { DateRangePickerNew } from "../../components/common/textFields/DatePickerRangeNew";
import { Dayjs } from "dayjs";
import { useSearchParams } from "react-router-dom";
import {
  parseQueryParamsForOpsAnalyticsFilters,
  OpsAnalyticsFilters,
  OpsAnalyticsSelectedFilters,
} from "../../../models/Dashboard/OpsDashboard/OpsAnalyticsFilters";

import AppDropDown from "../../components/common/filter/AppDropDown";
import AppButton from "../../components/common/buttons/AppButton";
import { useNotification } from "../../../utils/NotificationsContext";
import LoaderOverlay from "../../components/common/pages/AppLoader";
import { Admin } from "../../../models/TaskTracker/Admin";
import AppSearchDropDown2 from "../../components/common/filter/AppSearchDropdown2";
import PermissionBlockLabel from "../../components/common/pop-up/PermissionBlockLabel ";
import { EmployeeRoles } from "../../../models/Employees/EmployeeRoles";
import { SortType } from "../../../models/SortType";
import { FilterValue } from "../../../models/Filters/FilterValue";
import TypeFilterCard from "../../components/filters/TypeFilterCard";

const OPS_VENDORS_ANALYTICS_FILTERS_KEY = "OPS_SOURCING_ANALYTICS_FILTERS_KEY";

const excludedOrdersOptions = [
  {
    id: "false",
    value: "False",
  },
  {
    id: "true",
    value: "True",
  },
];

const OpsSourcingDashboard: React.FC = () => {
  const theme = useTheme();

  const {
    opsSourcingAnalyticsDashboardStore,
    projectStore,
    orderStore,
    TaskTrackerStore,
    userStore,
  } = useStores();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedFiltersData, setSelectedFiltersData] = useState<FilterValue[]>(
    []
  );
  const [loading, setLoading] = useState(false);
  // const [reloadList, setReloadList] = useState(false);
  const { showError } = useNotification();
  let apiTimer: any;

  const initialFilters: OpsAnalyticsFilters =
    parseQueryParamsForOpsAnalyticsFilters(
      localStorage.getItem(OPS_VENDORS_ANALYTICS_FILTERS_KEY)
        ? JSON.parse(localStorage.getItem(OPS_VENDORS_ANALYTICS_FILTERS_KEY)!)
        : null,
      searchParams
    );

  const [filters, setFilters] = useState<OpsAnalyticsFilters>(initialFilters);
  const [selectedFilters, setSelectedFilters] =
    useState<OpsAnalyticsSelectedFilters>({
      ...initialFilters,
      start_date: initialFilters.start_date.format("YYYY-MM-DD"),
      end_date: initialFilters.end_date.format("YYYY-MM-DD"),
    });

  useEffect(() => {
    updateSelectedFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  useEffect(() => {
    handleApply();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const newSearchParams = new URLSearchParams();
    Object.entries(selectedFilters).forEach(([key, value]) => {
      if (value) {
        if (Array.isArray(value)) {
          if (value.length > 0) {
            newSearchParams.set(key, value.map((item) => item.id).join(","));
          }
        } else {
          newSearchParams.set(key, value);
        }
      }
    });

    setSearchParams(newSearchParams);
  }, [selectedFilters]);

  const updateSelectedFilters = () => {
    localStorage.setItem(
      OPS_VENDORS_ANALYTICS_FILTERS_KEY,
      JSON.stringify(filters)
    );

    const start_date = filters.start_date.format("YYYY-MM-DD");
    const end_date = filters.end_date.format("YYYY-MM-DD");

    // if (
    //   selectedFilters.page !== filters.page ||
    //   selectedFilters.sortBy !== filters.sortBy ||
    //   selectedFilters.sortType !== filters.sortType
    // ) {
    //   setReloadList(true);
    // } else {
    //   setReloadList(false);
    // }

    setSelectedFilters((_) => ({
      ...filters,
      start_date: start_date,
      end_date: end_date,
    }));

    const newFilters: React.SetStateAction<FilterValue[]> = [];

    if (filters.pm_adminids) {
      filters.pm_adminids.forEach((pm_adminids) =>
        newFilters.push({ id: "pm_adminids", value: pm_adminids.value })
      );
    }

    if (filters.order_id) {
      filters.order_id.forEach((order_id) =>
        newFilters.push({ id: "order_id", value: order_id.value })
      );
    }
    if (filters.order_type) {
      filters.order_type.forEach((order_type) =>
        newFilters.push({ id: "order_type", value: order_type.value })
      );
    }
    if (filters.archived) {
      filters.archived.forEach((archived) =>
        newFilters.push({ id: "archived", value: archived.value })
      );
    }

    setSelectedFiltersData(newFilters);
  };

  useEffect(() => {
    projectStore.getTypeOfOrder();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleApply = async () => {
    setLoading(true);

    try {
      setFilters((prev) => ({
        ...prev,
        page: 1,
        limit: 30,
        search: "",
        sortBy: "created_date",
        sortType: SortType.DESC,
      }));

      await opsSourcingAnalyticsDashboardStore.getAllAnalyticsData({
        pm_adminids: selectedFilters.pm_adminids,
        archived: selectedFilters.archived,
        order_id: selectedFilters.order_id,
        order_type: selectedFilters.order_type,
        start_date: selectedFilters.start_date,
        end_date: selectedFilters.end_date,
        excluded_orders:
          selectedFilters.excluded_orders?.id === "true" ? true : false,
      });
    } catch (error) {
      showError("Error", "Failed to fetch the analytics dashboard details 2.");
    } finally {
      setTimeout(() => setLoading(false), 500);
    }
  };

  const handleRangeSelect = (range: {
    startDate: Dayjs | null;
    endDate: Dayjs | null;
  }) => {
    if (range.startDate && range.endDate) {
      setFilters((prev) => ({
        ...prev,
        start_date: range.startDate!,
        end_date: range.endDate!,
      }));
    }
  };

  const handleRemoveFilter = (index: number) => {
    const filterToRemove = selectedFiltersData[index];
    const updatedFilters = selectedFiltersData.filter((_, i) => i !== index);
    setSelectedFiltersData(updatedFilters);

    switch (filterToRemove.id) {
      case "pm_adminids":
        setFilters((prev) => ({
          ...prev,
          pm_adminids:
            filters.pm_adminids?.filter(
              (item) => item.value !== filterToRemove.value
            ) || [],
        }));
        break;

      case "order_type":
        setFilters((prev) => ({
          ...prev,
          order_type:
            prev.order_type?.filter(
              (item) => item.value !== filterToRemove.value
            ) || [],
        }));
        break;
      case "archived":
        setFilters((prev) => ({
          ...prev,
          archived:
            prev.archived?.filter(
              (item) => item.value !== filterToRemove.value
            ) || [],
        }));
        break;
      case "order_id":
        setFilters((prev) => ({
          ...prev,
          order_id:
            prev.order_id?.filter(
              (item) => item.value !== filterToRemove.value
            ) || [],
        }));
        break;
      default:
        break;
    }
  };

  const handleClearAllFilters = () => {
    setFilters((_) => ({
      ...initialFilters,
      pm_adminids: [],
      order_id: [],
      order_type: [],
      archived: [],
    }));

    setSelectedFiltersData([]);
  };

  const handleOrderIdSearch = (searchText?: string) => {
    if (apiTimer) clearTimeout(apiTimer);
    if (searchText && searchText.length > 0)
      apiTimer = setTimeout(() => {
        orderStore.searchOrderIds(searchText).catch((err) => {});
      }, 1500);
  };

  const handleAdminSearch = (searchText: string) => {
    if (searchText.length > 2) {
      TaskTrackerStore.getAdminBySearch(searchText).catch((err) => {
        showError("Error", err?.response?.data?.message);
      });
    }
  };

  return (
    <>
      {userStore.hasPermission(EmployeeRoles.VIEW_OPS_DASHBOARD) ? (
        <>
          <LoaderOverlay loading={loading} />
          <Box sx={{ margin: 4 }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                backgroundColor: theme.palette.customColors.white,
                padding: 4,
                borderRadius: 2,
                marginBottom: 1,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: "10px",
                }}
              >
                <Box sx={{ display: "flex", gap: 2 }}>
                  <Box sx={{ minWidth: "200px", width: "200px" }}>
                    <CustomTypography
                      variant="heading7"
                      color={theme.palette.customColors.tableGrayHeadingColor}
                      style={{ marginBottom: 10, fontSize: "0.8rem" }}
                    >
                      Date Range
                    </CustomTypography>
                    <DateRangePickerNew
                      onRangeSelect={handleRangeSelect}
                      startDate={filters.start_date}
                      endDate={filters.end_date}
                    />
                  </Box>

                  <Box sx={{ width: "15%", minWidth: "140px" }}>
                    <CustomTypography
                      variant="heading7"
                      color={theme.palette.customColors.tableGrayHeadingColor}
                      style={{ marginBottom: 10, fontSize: "0.8rem" }}
                    >
                      {"POC"}
                    </CustomTypography>
                    <AppSearchDropDown2
                      showSelection={false}
                      isMultiSelect={true}
                      title="Select Poc"
                      fontSize="14px"
                      value={filters.pm_adminids}
                      list={
                        TaskTrackerStore?.admins?.map((admin: Admin) => ({
                          id: admin.id.toString(),
                          value: admin.name,
                        })) || []
                      }
                      local={false}
                      handleSearch={(pm_adminids) =>
                        handleAdminSearch(pm_adminids!)
                      }
                      handleSelect={(items) => {
                        setFilters((prev) => ({
                          ...prev,
                          pm_adminids: items,
                        }));
                      }}
                    />
                  </Box>

                  <Box sx={{ width: "15%", minWidth: "140px" }}>
                    <CustomTypography
                      variant="heading7"
                      color={theme.palette.customColors.tableGrayHeadingColor}
                      style={{ marginBottom: 10, fontSize: "0.8rem" }}
                    >
                      {"Order Type"}
                    </CustomTypography>

                    <AppDropDown
                      title=""
                      isMultiSelect={true}
                      valuesList={
                        filters.order_type?.map((item) => item.id) || []
                      }
                      list={
                        projectStore?.orderType?.map((order) => ({
                          id: order.id.toString(),
                          value: order.value,
                        })) || []
                      }
                      fontSize={"12px"}
                      onMultiSelectionChange={(ids) => {
                        setFilters((prev) => ({
                          ...prev,
                          order_type:
                            projectStore?.orderType?.filter((item) =>
                              ids.includes(item.id)
                            ) || [],
                        }));
                      }}
                    />
                  </Box>

                  <Box sx={{ width: "15%", minWidth: "140px" }}>
                    <CustomTypography
                      variant="heading7"
                      color={theme.palette.customColors.tableGrayHeadingColor}
                      style={{ marginBottom: 10, fontSize: "0.8rem" }}
                    >
                      {"Project Status"}
                    </CustomTypography>

                    <AppDropDown
                      title=""
                      fontSize="0.7rem"
                      isMultiSelect={true}
                      valuesList={
                        filters.archived?.map((item) => item.id) || []
                      }
                      list={[{ id: "1", value: "Archived" }]}
                      onMultiSelectionChange={(ids) => {
                        setFilters((prev) => ({
                          ...prev,
                          archived: ids.map((id) => ({
                            id,
                            value: id === "1" ? "Archived" : "-1",
                          })),
                        }));
                      }}
                    />
                  </Box>

                  <Box sx={{ width: "15%", minWidth: "140px" }}>
                    <CustomTypography
                      variant="heading7"
                      color={theme.palette.customColors.tableGrayHeadingColor}
                      style={{ marginBottom: 10, fontSize: "0.8rem" }}
                    >
                      Order ID
                    </CustomTypography>

                    <AppSearchDropDown2
                      showSelection={false}
                      isMultiSelect={true}
                      title="Order Id"
                      value={filters.order_id}
                      fontSize="12px"
                      list={orderStore.orderIds.map((item) => {
                        return { id: String(item), value: String(item) };
                      })}
                      local={false}
                      handleSearch={handleOrderIdSearch}
                      handleSelect={(items) => {
                        setFilters((prev) => ({
                          ...prev,
                          order_id: items,
                        }));
                      }}
                    />
                  </Box>

                  <Box sx={{ width: "15%", minWidth: "140px" }}>
                    <CustomTypography
                      variant="heading7"
                      color={theme.palette.customColors.tableGrayHeadingColor}
                      style={{ marginBottom: 10, fontSize: "0.8rem" }}
                    >
                      {"Excluded orders"}
                    </CustomTypography>

                    <AppDropDown
                      title=""
                      isMultiSelect={false}
                      value={filters.excluded_orders?.id || "false"}
                      list={
                        excludedOrdersOptions.map((option) => ({
                          id: option.id,
                          value: option.value,
                        })) || []
                      }
                      fontSize={"12px"}
                      onSingleSelectionChange={(id) => {
                        setFilters((prev) => ({
                          ...prev,
                          excluded_orders:
                            excludedOrdersOptions.find(
                              (option) => option.id === id
                            ) || null,
                        }));
                      }}
                    />
                  </Box>
                </Box>

                <Box sx={{ display: "flex", gap: 2 }}>
                  <Box sx={{ marginTop: 3.1 }}>
                    <AppButton
                      variant="primary"
                      label="Apply"
                      onClick={handleApply}
                      disabled={loading}
                    />
                  </Box>

                  <Box sx={{ marginTop: 3.1 }}>
                    <AppButton
                      variant="secondary"
                      label="Clear"
                      customStyles={{ width: "100%" }}
                      onClick={() => handleClearAllFilters()}
                    />
                  </Box>
                </Box>
              </Box>

              <Box sx={{ paddingTop: "15px" }}>
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 2,
                    borderRadius: 2,
                    paddingLeft: "40px",
                    paddingRight: "40px",
                  }}
                >
                  {selectedFiltersData.map((filter, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        backgroundColor:
                          theme.palette.customColors.lightGreyColor,
                        borderRadius: 5,
                        border: "1px solid #E2E2E2",
                        height: "30px",
                        paddingRight: "5px",
                      }}
                    >
                      <CustomTypography
                        variant="heading8"
                        color={theme.palette.customColors.blackBorder}
                        style={{ paddingLeft: "10px", paddingRight: "5px" }}
                      >
                        {filter.value}
                      </CustomTypography>
                      <Button
                        onClick={() => handleRemoveFilter(index)}
                        sx={{
                          width: "14px",
                          height: "14px",
                          minWidth: "14px",
                          minHeight: "14px",
                          fontSize: "15px",
                          padding: "0px",
                          border: "1px solid #E2E2E2",
                          borderRadius: "50%",
                          backgroundColor: theme.palette.customColors.white,
                          cursor: "pointer",
                          overflow: "hidden",
                          "&:hover": {
                            backgroundColor:
                              theme.palette.customColors.lightGreyColor,
                          },
                        }}
                      >
                        ×
                      </Button>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Box>

            <Box sx={{ marginBottom: 3 }}>
              <CustomTypography
                variant="heading8"
                color={theme.palette.customColors.blackBorder}
              >
                <span style={{ fontStyle: "italic" }}>Note:</span> The analytics
                data is cached. Recent changes may take up to 30 minutes to
                reflect.
              </CustomTypography>
            </Box>

            <Box sx={{ gap: 6, display: "flex", flexDirection: "column" }}>
              <Box sx={{ gap: 2, display: "flex", flexDirection: "column" }}>
                {opsSourcingAnalyticsDashboardStore.pr_tats?.length > 0 && (
                  <Box sx={{ gap: 2, display: "flex", flexDirection: "row" }}>
                    <hr style={{ height: 1, width: "100%" }} />
                    <CustomTypography
                      variant="heading7"
                      color={theme.palette.customColors.blackBorder}
                      style={{
                        minWidth: "90px",
                        textAlign: "center",
                      }}
                    >
                      Raw Inward
                    </CustomTypography>
                    <hr style={{ height: 1, width: "100%" }} />
                  </Box>
                )}
                <Grid2
                  container
                  spacing={2}
                  columns={12}
                  sx={{ width: "100%" }}
                >
                  {opsSourcingAnalyticsDashboardStore.pr_tats?.map(
                    (item, index) => (
                      <Box
                        key={`${item.label}-${index}`}
                        sx={{
                          flex: "1 1 200px",
                          maxWidth: "300px",

                          "@media (min-width: 920px)": {
                            maxWidth: "calc(33.33% - 16px)",
                          },
                          "@media (max-width: 920px)": {
                            maxWidth: "calc(50% - 16px)",
                          },
                          "@media (max-width: 600px)": {
                            maxWidth: "100%",
                          },
                        }}
                      >
                        <TypeFilterCard
                          title={item.label}
                          message={item.info}
                          value={item.value ?? "0"}
                          color={""}
                          icon={
                            "https://content.swageazy.com/icons/boxes_green.png"
                          }
                          onClick={(title) => {
                            switch (title) {
                              case "Pending PRs":
                                const pending_prs_url = `/report/data?title=${encodeURI(
                                  "Pending PRs"
                                )}&base_url=/v5/ops-analytics/sourcing&path=/data/pending-prs&${searchParams.toString()}`;
                                window.open(pending_prs_url, "_blank");
                                break;
                              case "PR Creation Tat":
                                const dipatch_url = `/report/data?title=${encodeURI(
                                  "PR Creation Tat"
                                )}&base_url=/v5/ops-analytics/sourcing&path=/data/pr-create&${searchParams.toString()}`;
                                window.open(dipatch_url, "_blank");
                                break;
                              case "Received Supply Tat (w/o Customisation)":
                                const delivery_url = `/report/data?title=${encodeURI(
                                  "Received Supply Tat (w/o Customisation)"
                                )}&base_url=/v5/ops-analytics/sourcing&path=/data/raw-inward&${searchParams.toString()}`;
                                window.open(delivery_url, "_blank");
                                break;
                              case "Received Supply Tat (Customisation)":
                                const received_supply_url = `/report/data?title=${encodeURI(
                                  "Received Supply Tat (Customisation)"
                                )}&base_url=/v5/ops-analytics/sourcing&path=/data/print-inward&${searchParams.toString()}`;
                                window.open(received_supply_url, "_blank");
                                break;
                              case "Raw Inward Rejection %":
                                const raw_inward_url = `/report/data?title=${encodeURI(
                                  "Raw Inward Rejection %"
                                )}&base_url=/v5/ops-analytics/sourcing&path=/data/qc-rejections-percentage&${searchParams.toString()}`;
                                window.open(raw_inward_url, "_blank");
                                break;
                              case "Vendors Onboarded Count":
                                const vendor_tat_url = `/report/data?title=${encodeURI(
                                  "Vendors Onboarded Count"
                                )}&base_url=/v5/ops-analytics/sourcing&path=/data/vendors-onboarded&${searchParams.toString()}`;
                                window.open(vendor_tat_url, "_blank");
                                break;
                              default:
                                break;
                            }
                          }}
                        />
                      </Box>
                    )
                  )}
                </Grid2>
              </Box>
              <Box sx={{ gap: 2, display: "flex", flexDirection: "column" }}>
                {opsSourcingAnalyticsDashboardStore.po_tats?.length > 0 && (
                  <Box sx={{ gap: 2, display: "flex", flexDirection: "row" }}>
                    <hr style={{ height: 1, width: "100%" }} />
                    <CustomTypography
                      variant="heading7"
                      color={theme.palette.customColors.blackBorder}
                      style={{
                        minWidth: "90px",
                        textAlign: "center",
                      }}
                    >
                      POs
                    </CustomTypography>
                    <hr style={{ height: 1, width: "100%" }} />
                  </Box>
                )}
                <Grid2
                  container
                  spacing={2}
                  columns={12}
                  sx={{ width: "100%" }}
                >
                  {opsSourcingAnalyticsDashboardStore.po_tats?.map(
                    (item, index) => (
                      <Box
                        key={`${item.label}-${index}`}
                        sx={{
                          flex: "1 1 200px",
                          maxWidth: "300px",

                          "@media (min-width: 920px)": {
                            maxWidth: "calc(33.33% - 16px)",
                          },
                          "@media (max-width: 920px)": {
                            maxWidth: "calc(50% - 16px)",
                          },
                          "@media (max-width: 600px)": {
                            maxWidth: "100%",
                          },
                        }}
                      >
                        <TypeFilterCard
                          title={item.label}
                          message={item.info}
                          value={item.value ?? "0"}
                          color={""}
                          icon={
                            "https://content.swageazy.com/icons/boxes_green.png"
                          }
                          onClick={(title) => {
                            switch (title) {
                              case "Total POs":
                                const total_pos_url = `/report/data?title=${encodeURI(
                                  "Total POs"
                                )}&base_url=/v5/ops-analytics/sourcing&path=/data/total-po&${searchParams.toString()}`;
                                window.open(total_pos_url, "_blank");
                                break;
                              case "Completed POs":
                                const completed_pos_url = `/report/data?title=${encodeURI(
                                  "Completed POs"
                                )}&base_url=/v5/ops-analytics/sourcing&path=/data/completed-po&${searchParams.toString()}`;
                                window.open(completed_pos_url, "_blank");
                                break;
                              case "Pending POs":
                                const pending_pos_url = `/report/data?title=${encodeURI(
                                  "Pending POs"
                                )}&base_url=/v5/ops-analytics/sourcing&path=/data/pending-po&${searchParams.toString()}`;
                                window.open(pending_pos_url, "_blank");
                                break;
                              case "Products Item Quantity":
                                const products_item_quantity_url = `/report/data?title=${encodeURI(
                                  "Products Item Quantity"
                                )}&base_url=/v5/ops-analytics/sourcing&path=/data/po-products&${searchParams.toString()}`;
                                window.open(
                                  products_item_quantity_url,
                                  "_blank"
                                );
                                break;

                              case "Completed Inward Quantity":
                                const completed_inward_quantity_url = `/report/data?title=${encodeURI(
                                  "Completed Inward Quantity"
                                )}&base_url=/v5/ops-analytics/sourcing&path=/data/po-products-completed&${searchParams.toString()}`;
                                window.open(
                                  completed_inward_quantity_url,
                                  "_blank"
                                );
                                break;

                              case "Pending Inward Quantity":
                                const products_inward_quantity_url = `/report/data?title=${encodeURI(
                                  "Pending Inward Quantity"
                                )}&base_url=/v5/ops-analytics/sourcing&path=/data/po-products-pending&${searchParams.toString()}`;
                                window.open(
                                  products_inward_quantity_url,
                                  "_blank"
                                );
                                break;
                            }
                          }}
                        />
                      </Box>
                    )
                  )}
                </Grid2>
              </Box>
            </Box>
          </Box>
        </>
      ) : (
        <PermissionBlockLabel />
      )}
    </>
  );
};

export default observer(OpsSourcingDashboard);
